import { type CaptureHostInputs, type MicReadinessSource } from '../ports/host-inputs';
import { getConfiguredHostInputs, onCaptureConfigurationChange } from './configuration';

const listeners = new Set<() => void>();
let attachedSource: MicReadinessSource | null = null;
let detachFromSource: (() => void) | null = null;

const notifyAll = (): void => {
  listeners.forEach((listener) => listener());
};

const attachTo = (source: MicReadinessSource): void => {
  detachFromSource?.();
  attachedSource = source;
  detachFromSource = source.subscribe(notifyAll);
};

// Reconfiguration swaps the underlying source behind the stable facade:
// subscribers detach from the old source, attach to the replacement, and are
// notified once so consumers re-read the snapshot.
onCaptureConfigurationChange(() => {
  const source = getConfiguredHostInputs().micReadiness;
  if (attachedSource === null || source === attachedSource) return;
  attachTo(source);
  notifyAll();
});

// The stable external-store facade `useAudioLevel` consumes via
// `useSyncExternalStore`. Its identity never changes; only the delegated
// source does (last write wins).
export const micReadinessFacade: MicReadinessSource = {
  getSnapshot: () => getConfiguredHostInputs().micReadiness.getSnapshot(),
  setReady: (ready) => getConfiguredHostInputs().micReadiness.setReady(ready),
  subscribe: (listener) => {
    listeners.add(listener);
    const source = getConfiguredHostInputs().micReadiness;
    if (attachedSource !== source || detachFromSource === null) {
      attachTo(source);
    }
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) {
        detachFromSource?.();
        detachFromSource = null;
        attachedSource = null;
      }
    };
  },
};

export const captureHostInputs: Pick<CaptureHostInputs, 'isNativeCaptureHost'> = {
  isNativeCaptureHost: () => getConfiguredHostInputs().isNativeCaptureHost(),
};

export const micSelectorHost = {
  useTutorial: () => getConfiguredHostInputs().micSelector.useTutorial(),
  useCanChangeMicrophone: () =>
    getConfiguredHostInputs().micSelector.useCanChangeMicrophone(),
  useIsTabSharingSession: () =>
    getConfiguredHostInputs().micSelector.useIsTabSharingSession(),
  useCopy: () => getConfiguredHostInputs().micSelector.useCopy(),
  useNativeAudioLevels: () =>
    getConfiguredHostInputs().micSelector.useNativeAudioLevels(),
  useChronicleMeter: () => getConfiguredHostInputs().micSelector.useChronicleMeter(),
  get LegacyMicrophoneSelector() {
    return getConfiguredHostInputs().micSelector.LegacyMicrophoneSelector;
  },
  get MicTestModal() {
    return getConfiguredHostInputs().micSelector.MicTestModal;
  },
  get SpeakerTestModal() {
    return getConfiguredHostInputs().micSelector.SpeakerTestModal;
  },
  get MobileNavBar() {
    return getConfiguredHostInputs().micSelector.MobileNavBar;
  },
  reportWebAudioLevelAlert: (
    owner: symbol,
    recordingId: string | null,
    open: boolean
  ) => {
    getConfiguredHostInputs().micSelector.reportWebAudioLevelAlert?.(
      owner,
      recordingId,
      open
    );
  },
};
